import { type FormEvent, useCallback, useMemo, useState } from "react";
import type { PortalApi, RunDetail, RunFields, RunState, TranscriptPage } from "../api.ts";
import { durationText, segmentSeconds } from "../duration.ts";
import { Id } from "../Id.tsx";
import { LiveFacts, LiveModeNote, LiveSummary } from "../LiveState.tsx";
import { LocalTime, TimeZoneNote } from "../LocalTime.tsx";
import { type LiveFeed, pollingOnly, useLiveStates, useNow } from "../live.ts";
import { messageOf, useLoad } from "../load.ts";
import { moneyText } from "../money.ts";
import { localDay, localDayWindow, localTimeZone } from "../time.ts";
import {
  Button,
  cx,
  Field,
  facts,
  Input,
  Notice,
  PageLayout,
  Table,
  TitleId,
  Toolbar,
} from "../ui.tsx";

/**
 * The operators' run browser (docs/roadmap.md#admin-portal): a day's runs from the day index → a
 * run with its segments and the sessions that named it → a session's transcript, page by page, and
 * a segment's recording package to download. Capture data, so operators only. The day's runs and a
 * run show their live state (docs/carrier-runs.md#live-state) as it changes: through the data
 * API's subscriptions, or by polling when those are unavailable.
 *
 * Days and times are the browser's local ones: a day is from local midnight to the next (today by
 * default), and every time shows in local time.
 *
 * Addresses: `#/runs/day/<local date>`, `#/runs/run/<runId>`, `#/runs/session/<sessionId>`.
 */

export const runLink = (runId: string) => `#/runs/run/${encodeURIComponent(runId)}`;
const dayLink = (date: string) => `#/runs/day/${date}`;
const sessionLink = (sessionId: string) => `#/runs/session/${encodeURIComponent(sessionId)}`;

const NO_FEED = pollingOnly("not connected");

export function RunsPage({
  api,
  path,
  live = NO_FEED,
}: {
  api: PortalApi;
  path: readonly string[];
  /** The live state's subscriptions; polling only without them. */
  live?: LiveFeed;
}) {
  const [view, id] = path;
  if (view === "run" && id) return <RunView key={id} api={api} live={live} runId={id} />;
  if (view === "session" && id) return <SessionView key={id} api={api} sessionId={id} />;
  const date = view === "day" && id && /^\d{4}-\d{2}-\d{2}$/.test(id) ? id : undefined;
  return <DayView key={date ?? "default"} api={api} live={live} date={date ?? localDay()} />;
}

const livesOf = (runs: readonly { live?: RunState | undefined }[]): RunState[] =>
  runs.flatMap((entry) => (entry.live ? [entry.live] : []));

function DayView({ api, live, date }: { api: PortalApi; live: LiveFeed; date: string }) {
  const [draft, setDraft] = useState(date);
  const { from, to } = useMemo(() => localDayWindow(date), [date]);
  const day = useLoad(() => api.runsOfDay(from, to), [api, from, to]);
  const initial = useMemo(() => livesOf(day.data?.runs ?? []), [day.data]);
  const poll = useCallback(
    async () => livesOf((await api.runsOfDay(from, to)).runs),
    [api, from, to],
  );
  const { states, mode, reason } = useLiveStates(live, "all", initial, poll);
  const now = useNow();
  const go = (event: FormEvent) => {
    event.preventDefault();
    if (draft) window.location.hash = dayLink(draft);
  };
  return (
    <PageLayout title="Runs">
      <Toolbar onSubmit={go}>
        <Field label="Day">
          <Input type="date" value={draft} onChange={(event) => setDraft(event.target.value)} />
        </Field>
        <Button type="submit">Show</Button>
        <span className="text-muted text-ui">
          runs with a segment started that day, from local midnight ({localTimeZone()})
        </span>
      </Toolbar>
      {day.loading && <p className="text-muted">Loading…</p>}
      {day.error && <Notice tone="bad">{day.error}</Notice>}
      {day.data && day.data.runs.length === 0 && (
        <p className="text-muted">No segment started on {date}.</p>
      )}
      {day.data && day.data.runs.length > 0 && (
        <>
          {day.data.truncated && (
            <Notice tone="bad">The day index read hit its bound: some segments are missing.</Notice>
          )}
          <LiveModeNote mode={mode} reason={reason} />
          <Table>
            <thead>
              <tr>
                <th>Run</th>
                <th>Carrier</th>
                <th>Status</th>
                <th>Live</th>
                <th>First start</th>
                <th>Last end</th>
                <th>Elapsed · active</th>
                <th title="Segments started that day, how many are open, how many have a recording and how many were joined to a session">
                  Segments
                </th>
                <th>Producers</th>
              </tr>
            </thead>
            <tbody>
              {day.data.runs.map((entry) => {
                const run = entry.run as RunFields | undefined;
                return (
                  <tr key={entry.runId}>
                    <td>
                      <Id id={entry.runId} href={runLink(entry.runId)} />
                    </td>
                    <td className="whitespace-normal">{run?.carrier ?? "—"}</td>
                    <td>{run?.status ?? "—"}</td>
                    <td className="min-w-44 whitespace-normal">
                      <LiveSummary state={states.get(entry.runId)} now={now} />
                    </td>
                    <td>
                      <LocalTime iso={entry.firstStartedAt} />
                    </td>
                    <td>{entry.lastEndedAt ? <LocalTime iso={entry.lastEndedAt} /> : "—"}</td>
                    <td>
                      {run?.timing
                        ? `${durationText(run.timing.elapsedSeconds)} · ${durationText(run.timing.activeSeconds)}`
                        : "—"}
                    </td>
                    <td>
                      <SegmentCounts entry={entry} />
                    </td>
                    <td>
                      <code>{entry.producerIds.join(", ")}</code>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
          {day.data.omitted > 0 && <p className="text-muted">and {day.data.omitted} more runs</p>}
        </>
      )}
    </PageLayout>
  );
}

function RunView({ api, live, runId }: { api: PortalApi; live: LiveFeed; runId: string }) {
  const detail = useLoad(() => api.runDetail(runId), [api, runId]);
  const initial = useMemo(() => (detail.data?.live ? [detail.data.live] : []), [detail.data]);
  const poll = useCallback(async () => {
    const state = await api.runState(runId);
    return state ? [state] : [];
  }, [api, runId]);
  const { states, mode, reason } = useLiveStates(live, { runId }, initial, poll);
  const now = useNow();
  return (
    <PageLayout
      back={{ href: "#/runs", label: "← Runs" }}
      title={
        <>
          Run{" "}
          <TitleId>
            <Id id={runId} />
          </TitleId>
        </>
      }
    >
      <TimeZoneNote />
      {detail.loading && <p className="text-muted">Loading…</p>}
      {detail.error && <Notice tone="bad">{detail.error}</Notice>}
      {detail.data && (
        <>
          <h2>Live</h2>
          <LiveModeNote mode={mode} reason={reason} />
          <LiveFacts state={states.get(runId)} now={now} />
          <RunBody api={api} detail={detail.data} />
        </>
      )}
    </PageLayout>
  );
}

function RunBody({ api, detail }: { api: PortalApi; detail: RunDetail }) {
  const { segments, sessions } = detail;
  const run = detail.run as unknown as RunFields;
  const { timing } = run;
  const ended = timing?.endedAt ?? run.endedAt;
  const quoteStatus = detail.quote?.status as string | undefined;
  const [download, setDownload] = useState<{ text: string; error?: boolean }>();

  const fetchRecording = async (segmentId: string) => {
    setDownload(undefined);
    try {
      const { url } = await api.recordingDownload(run.id, segmentId);
      window.location.assign(url);
    } catch (error) {
      setDownload({ text: messageOf(error), error: true });
    }
  };

  return (
    <>
      <dl className={facts}>
        <dt>Carrier</dt>
        <dd>{run.carrier ?? "—"}</dd>
        <dt>Status</dt>
        <dd>{run.status ?? "—"}</dd>
        <dt>Quote</dt>
        <dd>
          <Id id={run.quoteId} />
          {quoteStatus ? ` (${quoteStatus})` : ""}
        </dd>
        <dt>Premium</dt>
        <dd>{run.premium === undefined ? "—" : moneyText(run.premium)}</dd>
        <dt>Started</dt>
        <dd>
          <LocalTime iso={timing?.startedAt ?? run.startedAt ?? run.createdAt} />
        </dd>
        <dt>Ended</dt>
        <dd>{ended ? <LocalTime iso={ended} /> : "open"}</dd>
        {run.firstQuotedAt && (
          <>
            <dt>First quoted</dt>
            <dd>
              <LocalTime iso={run.firstQuotedAt} />
            </dd>
          </>
        )}
        <dt>Elapsed</dt>
        <dd>
          {timing
            ? `${durationText(timing.elapsedSeconds)}${timing.endedAt ? "" : " so far"}`
            : "—"}
        </dd>
        <dt>Active</dt>
        <dd>
          {timing ? (
            <>
              {durationText(timing.activeSeconds)} in {timing.segments} segments
              {timing.openSegments > 0 && (
                <span className="text-muted"> ({timing.openSegments} open, not counted yet)</span>
              )}
            </>
          ) : (
            "—"
          )}
        </dd>
        <dt>Sessions</dt>
        <dd>{timing?.sessions ?? "—"}</dd>
        <dt>Last page</dt>
        <dd>{run.lastPageTitle ?? run.lastUrl ?? "—"}</dd>
      </dl>

      <h2>Segments ({segments.length})</h2>
      {download && <Notice tone={download.error ? "bad" : "ok"}>{download.text}</Notice>}
      <Table>
        <thead>
          <tr>
            <th>Segment</th>
            <th>Started</th>
            <th>Ended</th>
            <th>Duration</th>
            <th>Producer</th>
            <th>Recording</th>
            <th>Session</th>
          </tr>
        </thead>
        <tbody>
          {segments.map((segment) => (
            <tr key={segment.segmentId}>
              <td>
                <Id id={segment.segmentId} />
              </td>
              <td>
                <LocalTime iso={segment.startedAt} />
              </td>
              <td>{segment.endedAt ? <LocalTime iso={segment.endedAt} /> : "open"}</td>
              <td>{segment.endedAt ? durationText(segmentSeconds(segment)) : "—"}</td>
              <td>
                <code>{segment.producerId ?? "—"}</code>
              </td>
              <td className="min-w-48 whitespace-normal">
                {segment.recording ? (
                  <>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => fetchRecording(segment.segmentId)}
                    >
                      Download
                    </Button>{" "}
                    {segment.recordingBytes !== undefined && `${kb(segment.recordingBytes)} · `}
                    {segment.recordingSummary
                      ? `${segment.recordingSummary.calls} calls, ${segment.recordingSummary.network} requests, ${segment.recordingSummary.screenshots} screenshots${segment.recordingSummary.packageCapReached ? ", cap reached" : ""}`
                      : null}
                    {segment.recordingProblem && (
                      <span className="text-bad"> {segment.recordingProblem}</span>
                    )}
                  </>
                ) : (
                  <span className="text-bad">none</span>
                )}
              </td>
              <td>
                {segment.sessionId ? (
                  <Id id={segment.sessionId} href={sessionLink(segment.sessionId)} />
                ) : (
                  <span className="text-bad">none joined</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <h2>Sessions that named the run ({sessions.length})</h2>
      {sessions.length === 0 ? (
        <p className="text-muted">No transcript has named this run.</p>
      ) : (
        <Table>
          <thead>
            <tr>
              <th>Session</th>
              <th>Producer</th>
              <th>Started the run</th>
              <th>Calls on the run</th>
              <th>Lines</th>
            </tr>
          </thead>
          <tbody>
            {sessions.map((entry) => (
              <tr key={entry.sessionId}>
                <td>
                  <Id id={entry.sessionId} href={sessionLink(entry.sessionId)} />
                </td>
                <td>
                  <code>{entry.producerId ?? "—"}</code>
                </td>
                <td>{entry.startedRun ? "yes" : "no"}</td>
                <td>
                  {entry.spans.length === 0
                    ? "—"
                    : entry.spans.map((span, i) => (
                        <span key={`${span.from}-${span.to}`}>
                          {i > 0 && "; "}
                          <LocalTime iso={span.from} /> – <LocalTime iso={span.to} />
                        </span>
                      ))}
                </td>
                <td>{String(entry.session?.lineCount ?? "—")}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {detail.omittedSessions > 0 && (
        <p className="text-muted">and {detail.omittedSessions} more sessions</p>
      )}
    </>
  );
}

type DayRun = Awaited<ReturnType<PortalApi["runsOfDay"]>>["runs"][number];

/**
 * A day's run's segments in one cell: how many, how many are open, have a recording and were
 * joined to a session; a recording or a session missing from an ended segment in red.
 */
function SegmentCounts({ entry }: { entry: DayRun }) {
  const ended = entry.segments - entry.open;
  return (
    <>
      {entry.segments}
      <span className="text-muted"> · {entry.open} open · </span>
      <span className={entry.recordings < ended ? "text-bad" : "text-muted"}>
        {entry.recordings} <abbr title="with a recording">rec</abbr>
      </span>
      <span className="text-muted"> · </span>
      <span className={entry.joined < ended ? "text-bad" : "text-muted"}>
        {entry.joined} <abbr title="joined to a session">joined</abbr>
      </span>
    </>
  );
}

function kb(bytes: number): string {
  return bytes < 1024 * 1024
    ? `${Math.ceil(bytes / 1024)} KB`
    : `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function SessionView({ api, sessionId }: { api: PortalApi; sessionId: string }) {
  const first = useLoad(() => api.sessionTranscript(sessionId), [api, sessionId]);
  const [more, setMore] = useState<TranscriptPage[]>([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState<string>();
  const pages = first.data ? [first.data, ...more] : [];
  const last = pages.at(-1);

  const next = async () => {
    if (last?.nextOffset === undefined) return;
    setLoadingMore(true);
    setMoreError(undefined);
    try {
      const page = await api.sessionTranscript(sessionId, last.nextOffset);
      setMore((loaded) => [...loaded, page]);
    } catch (error) {
      setMoreError(messageOf(error));
    }
    setLoadingMore(false);
  };

  return (
    <PageLayout
      back={{ href: "#/runs", label: "← Runs" }}
      title={
        <>
          Session{" "}
          <TitleId>
            <Id id={sessionId} />
          </TitleId>
        </>
      }
    >
      <TimeZoneNote />
      {first.loading && <p className="text-muted">Loading…</p>}
      {first.error && <Notice tone="bad">{first.error}</Notice>}
      {first.data && (
        <p className="text-muted">
          {first.data.batches} transcript objects, {first.data.endOffset} bytes received.
        </p>
      )}
      {first.data && first.data.batches === 0 && (
        <p className="text-muted">No transcript of this session has arrived.</p>
      )}
      <ol className="my-4 border-line border-t">
        {pages.flatMap((page) => [
          ...page.missing.map((gap) => (
            <li key={`gap-${gap.from}`} className="my-2 rounded-md bg-bad-tint px-3 py-2 text-bad">
              Bytes {gap.from}–{gap.to} have not arrived.
            </li>
          )),
          ...page.lines.map((line) => <TranscriptLineView key={line.offset} line={line} />),
        ])}
      </ol>
      {moreError && <Notice tone="bad">{moreError}</Notice>}
      {last?.nextOffset !== undefined && (
        <Button type="button" onClick={next} disabled={loadingMore}>
          {loadingMore ? "Loading…" : "More"}
        </Button>
      )}
    </PageLayout>
  );
}

function TranscriptLineView({ line }: { line: TranscriptPage["lines"][number] }) {
  const kind = line.prompt ? "producer" : (line.type ?? "?");
  return (
    <li
      className={cx(
        "border-line border-b px-3.5 py-3",
        line.prompt && "bg-prompt",
        line.sidechain && "ml-4 border-l-2 border-l-line-strong",
      )}
    >
      <div className="mb-1 text-muted text-xs">
        <strong
          className={cx(
            "mr-1.5 inline-block rounded-sm px-1.5 font-medium text-xs/[18px]",
            line.prompt ? "bg-accent-solid text-on-accent" : "bg-tag text-fg",
          )}
        >
          {kind}
        </strong>{" "}
        {line.timestamp && <LocalTime iso={line.timestamp} />}
        {line.sidechain && " · subagent"}
        {line.cut && " · cut"}
      </div>
      {line.problem && <div className="my-1 text-bad">{line.problem}</div>}
      {line.text && (
        <pre className="wrap-anywhere my-1 whitespace-pre-wrap font-sans text-sm/relaxed">
          {line.text}
        </pre>
      )}
      {line.thinking && (
        <details className="mt-1.5">
          <summary>thinking</summary>
          <pre>{line.thinking}</pre>
        </details>
      )}
      {line.toolCalls.map((call, i) => (
        <details className="mt-1.5" key={call.id ?? `call-${i}`}>
          <summary>
            call <code>{call.name}</code>
          </summary>
          <pre>{call.input}</pre>
        </details>
      ))}
      {line.toolResults.map((result, i) => (
        <details className="mt-1.5" key={result.toolUseId ?? `result-${i}`}>
          <summary className={result.error ? "text-bad" : undefined}>
            result{result.error ? " (error)" : ""}
          </summary>
          <pre>{result.text}</pre>
        </details>
      ))}
    </li>
  );
}
